// Libs
import React from "react";
import { createRoot } from "react-dom/client";

import { QueryClientProvider } from "@tanstack/react-query";
import { NewQueryClient } from "utils/queryClient";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import OneSignal from "react-onesignal";

// Redux
import { Provider } from "react-redux";
import { store } from "utils/store/configureStore";

// Context
import { UserProvider } from "contexts/UserContext";

// Analytics Libs
import ReactGA from "react-ga4";
import ReactPixel from "react-facebook-pixel";
import { GoogleTagManager } from "components/GoogleTagManager";
import { AnalyticsProvider } from "use-analytics";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";

// Components
import Routes from "./routes";
import "utils/i18n";
// Styles
import { ThemeProvider } from "styled-components";
import theme from "components/spocketUI/theme";

// Utils
import { analytics } from "utils/analytics";
import ErrorBoundary from "utils/errorBoundary";
import registerServiceWorker from "utils/registerServiceWorker";

if (process.env.NODE_ENV === "production" || process.env.NODE_ENV === "staging") {
  ReactGA.initialize(process.env.REACT_APP_GA_KEY, {
    gaOptions: {
      allowLinker: true,
      cookieDomain: "auto"
    }
  });
  ReactGA.ga("require", "linker");
  ReactGA.ga("linker:autoLink", ["shopify.com", "shopify.ca"]);
  ReactGA.gtag("consent", "default", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted"
  });
}

// MSW - Mock Service Worker
if (process.env.NODE_ENV === "staging") {
  const { worker } = require("./mocks/browser");
  worker.start();
}

// React Facebook Pixel
ReactPixel.init(process.env.REACT_APP_FACEBOOK_PIXEL, {}, { debug: false, autoConfig: false });

const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const isDevelopment = process.env.NODE_ENV === "development";
const datadogAppId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
const datadogToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;

if (!isDevelopment && !isLocalhost && datadogAppId && datadogToken) {
  datadogRum.init({
    applicationId: datadogAppId,
    clientToken: datadogToken,
    site: process.env.REACT_APP_DATADOG_SITE || "datadoghq.com",
    service: "dropshipper-app",
    env: process.env.REACT_APP_ENV || process.env.NODE_ENV,
    version: "1.0.0",
    sessionSampleRate: 30,
    sessionReplaySampleRate: 0,
    trackUserInteractions: false,
    trackResources: false,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input"
  });

  datadogLogs.init({
    clientToken: datadogToken,
    site: process.env.REACT_APP_DATADOG_SITE || "datadoghq.com",
    service: "dropshipper-app",
    env: process.env.REACT_APP_ENV || process.env.NODE_ENV,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error", "warn"],
    sessionSampleRate: 100
  });

  console.log("Datadog initialized:", { env: process.env.REACT_APP_ENV });
} else {
  console.log("Datadog disabled (development or missing credentials)");
}

const shouldLoadOneSignal =
  process.env.RECT_APP_LOAD_ONESIGNAL === true || process.env.RECT_APP_LOAD_ONESIGNAL === "true";

if (shouldLoadOneSignal) {
  OneSignal.init({
    appId: process.env.REACT_APP_ONESIGNAL_APP_ID,
    notifyButton: { enable: true },
    allowLocalhostAsSecureOrigin: process.env.NODE_ENV === "development"
  });
}

// Avoiding frame busting in app
if (window.top !== window.self) {
  window.top.location = window.self.location;
}

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <ErrorBoundary>
    <AnalyticsProvider instance={analytics}>
      <QueryClientProvider client={NewQueryClient}>
        <Provider store={store}>
          <UserProvider>
            <ThemeProvider theme={theme}>
              {process.env.NODE_ENV === "production" && (
                <GoogleTagManager gtmId={process.env.REACT_APP_GTM_ID} />
              )}

              <Routes />
            </ThemeProvider>
          </UserProvider>
        </Provider>
        <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
      </QueryClientProvider>
    </AnalyticsProvider>
  </ErrorBoundary>
);

registerServiceWorker();
